import React, { useRef, useState, useContext, useEffect, useCallback } from "react";
import axios from "axios";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useUser } from "~/Context/userContext";
import { useSettings } from "~/Context/SettingsContext";
import TrackImage from "./Global/TrackImage";
import Container from "react-bootstrap/Container";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fab } from "@fortawesome/free-brands-svg-icons";
import {
  faPlay,
  faPause,
  faStop,
  faForward,
  faBackward,
  faSpinner,
  faVolumeUp,
  faThList,
  faSearchPlus,
  faSearchMinus,
  faExpandAlt,
  faExpandArrowsAlt as faExpandArrows,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { store } from "~/Context/JukeboxContext";
import Duration from "./Duration";
import YouTubePlayer from "react-player/lib/players/YouTube";
import SoundCloud from "react-player/lib/players/SoundCloud";
import MediaSourceIcon from "~/Components/Icons/MediaSourceIcon";
import AddToPlaylistModal from "./AddToPlaylistModal";
import { getFavoritePlaylists } from "~/api/getCustomPlaylists";

import { isSoundCloudMediaUrl, isYouTubeMediaUrl } from "../../utils/media";
import Toggle from "react-toggle";
import "react-toggle/style.css";
import "../../css/Jukebox.less";
// import { genrePlaylists } from "~/utils/genrePlaylists";

library.add(fab);

const JUKEBOX_URL = `https://www.dreamchimney.com/tracks/jukebox/playlists`;
const JUKEBOX_LEGACY_URL = `//www.dreamchimney.com/tracks/api.php`;
const RADIO_CURRENT_TRACK_URL = `https://api.radioking.io/widget/radio/dream-chimney-radio/track/current`;

// get all the playlists
// http://www.dreamchimney.com/tracks/api.php?route=totd_playlists&chim=ryan

// http://www.dreamchimney.com/tracks/jukebox/playlists/most-recent.json?v=1577835637559

// get a single track (includes last 20)
// http://www.dreamchimney.com/tracks/api.php?route=single_track&track_id=41214&1578031837761

// Styled components for progress slider
const ProgressSliderWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 80%;
  margin-top: 8px;
  gap: 10px;
`;

const TimeLabel = styled.span`
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
  min-width: 45px;
  text-align: ${(props) => (props.align === "right" ? "right" : "left")};
`;

const ProgressBarContainer = styled.div`
  position: relative;
  flex: 1;
  height: 4px;
  background-color: #555;
  border-radius: 2px;
  cursor: pointer;
  user-select: none;
  &:hover {
    background-color: #666;
  }
`;

const ProgressBarFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background-color: #f3a92a;
  border-radius: 2px;
  width: ${(props) => props.progress}%;
  transition: width 0.1s linear;
`;

// Utility function to calculate seek progress from click/drag position
const calculateSeekProgress = (event, sliderRef) => {
  if (!sliderRef.current) return 0;
  const rect = sliderRef.current.getBoundingClientRect();
  const clickX = event.clientX - rect.left;
  const sliderWidth = rect.width;
  const progress = Math.max(0, Math.min(1, clickX / sliderWidth));
  return parseFloat(progress);
};

function Jukebox() {
  const thePlayer = useRef(null);
  const progressSliderRef = useRef(null);
  const { settings } = useSettings();
  const { state: userState } = useUser();

  const [loading, setLoading] = useState(true);
  const [duration, setDuration] = useState(0);
  const [playlist, setPlaylist] = useState({ name: "", tracks: [] });
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [played, setPlayed] = useState(0);
  const [volume, setVolume] = useState(1);
  const [currentTrack, setCurrentTrack] = useState({});
  const [videoExpand, setVideoExpand] = useState(false);
  const videoContainerRef = useRef(null);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);
  const [seekToOnReady, setSeekToOnReady] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showAddToPlaylistModal, setShowAddToPlaylistModal] = useState(false);
  const [favoritePlaylists, setFavoritePlaylists] = useState([]);
  const addToPlaylistModalRef = useRef(null);

  const [radioCurrentTrack, setRadioCurrentTrack] = useState(null);

  const globalState = useContext(store);
  const { dispatch } = globalState;

  const cacheBuster = new Date().getTime();

  const longText = (text, max) => {
    text = text.trim();
    if (text && text.length > max) {
      // strip leading and trailing whitespace
      return text.substr(0, max) + "...";
    }
    return text;
  };

  /**
   * load most recent tracks
   * @param {*} playing auto starts the playlist
   */
  const getPlaylistMostRecent = async (playing = false) => {
    setLoading(true);
    await axios.get(`${JUKEBOX_URL}/most-recent.json?v=${cacheBuster}`).then(({ data }) => {
      // if ryan, filter out spider tracks
      let playlist = data[0];
      if (isLoggedin() && userState.user && userState.user.dir === "ryan") {
        const tracks = playlist.tracks.filter((p) => p.chimdir !== "spider");
        playlist = { ...playlist, tracks };
      }
      setPlaylist(playlist); // shape of response is odd. its an array with object
      dispatch({ type: "load_playlist", playlist: playlist, playing });
      setLoading(false);
    });
  };

  /**
   * load favorites playlist
   * @param {*} playing auto starts the playlist
   */
  const getPlaylistFavorites = async (playing = false) => {
    setLoading(true);
    await axios.get(`${JUKEBOX_URL}/favorites_${userState.user.dir}.json?v=${cacheBuster}`).then(({ data }) => {
      setPlaylist(data[0]); // shape of response is odd. its an array with object
      dispatch({ type: "load_playlist", playlist: data[0], playing });
      setLoading(false);
    });
  };
  /**
   * load radio as a playlist
   * @param {*} playing auto starts the playlist
   */
  const getPlaylistRadio = (playing = false) => {
    setLoading(true);

    // fake radio playlist
    const radioPlaylist = {
      tracks: [
        {
          id: "0",
          date: "02.27.2021",
          title: "Radio",
          artist: "Radio",
          chimdir: "ryan",
          sc_url: "",
          genre_id: "610",
          genre: "Baked Electronics",
          media_type: "radio",
          media_id: "",
        },
      ],
    };

    dispatch({ type: "load_playlist", playlist: radioPlaylist, playing });
    setPlaylist(radioPlaylist); // shape of response is odd. its an array with object
    setTimeout(() => {
      setLoading(false);
    }, 4000);
  };

  /**
   * load favorites playlist
   * @param {*} playing auto starts the playlist
   */
  const getPlaylistUserMostRecent = async (playing = false) => {
    setLoading(true);
    const { data } = await axios.get(`${JUKEBOX_URL}/most-recent_${userState.user.dir}.json?v=${cacheBuster}`);
    setPlaylist(data[0]); // shape of response is odd. its an array with object
    dispatch({ type: "load_playlist", playlist: data[0], playing });
    setLoading(false);
  };
  /**
   * load genre playlist
   * @param {*} playing auto starts the playlist, 183
   */
  const getPlaylistGenre = async (genreId, playing = false) => {
    setLoading(true);
    const { data } = await axios.get(`${JUKEBOX_LEGACY_URL}?route=genre_playlist&genre_id=${genreId}`);
    setPlaylist(data[0]); // shape of response is odd. its an array with object
    dispatch({ type: "load_playlist", playlist: data[0], playing });
    setLoading(false);
  };

  // youtube track was stopping at like 0.997123123121332 instead of 1
  const roundPlayedCounter = (state) => Math.round(state.played * 1000) / 1000;

  const trackCompleted = (state) => roundPlayedCounter(state) >= 0.99 || (playing && state.played === 0 && state.loaded > 0);

  useEffect(() => {
    if (userState.loaded) {
      getPlaylistMostRecent();
    }
  }, [userState.loaded]);

  useEffect(() => {
    if (!userState.loggedin) {
      setFavoritePlaylists([]);
      return undefined;
    }
    let cancelled = false;
    getFavoritePlaylists(axios, 50)
      .then(({ data }) => {
        if (!cancelled) setFavoritePlaylists(data.playlists || []);
      })
      .catch(() => {
        if (!cancelled) setFavoritePlaylists([]);
      });
    return () => {
      cancelled = true;
    };
  }, [userState.loggedin]);

  const getFavoritePlaylist = async (playlistId, playlistName) => {
    setLoading(true);
    const { data } = await axios.get(`//www.dreamchimney.com/api/playlist-custom?id=${playlistId}`);
    const loaded = (data && data[0]) || { tracks: [] };
    if (loaded.tracks && loaded.tracks.length) {
      const next = { ...loaded, name: playlistName, autoAdvance: true };
      setPlaylist(next);
      dispatch({ type: "load_playlist", playlist: next, playing: true });
    }
    setLoading(false);
  };

  const getCurrentTrack = (state) => {
    if (state.playing === true && Object.keys(state.currentTrack).length === 0) {
      const position = state.position || 0;
      return (state.tracks && state.tracks[position]) || {};
    }
    return state.currentTrack;
  };

  const handleChangePlaylist = (event) => {
    const value = event.target.value;

    if (value === "add_to_playlist") {
      if (playing && currentTrack.id && !isRadio) {
        // Trigger the modal by setting state
        setShowAddToPlaylistModal(true);
        // Reset select to show current playlist
        event.target.value = "";
        event.target.selectedIndex = 0;
      }
      return;
    }

    if (value === "radio") {
      getPlaylistRadio(true);
    }
    if (value === "favorites") {
      getPlaylistFavorites(true);
    }
    if (value === "most_recent") {
      getPlaylistMostRecent(true);
    }
    if (value === "user_most_recent") {
      getPlaylistUserMostRecent(true);
    }
    if (value.startsWith("genre-")) {
      const genreId = value.replace(/genre-/gi, "");
      getPlaylistGenre(genreId, true);
    }
    if (value.startsWith("fav-playlist-")) {
      const playlistId = value.replace("fav-playlist-", "");
      const match = favoritePlaylists.find((p) => String(p.id) === playlistId);
      getFavoritePlaylist(playlistId, match ? match.name : "Playlist");
    }
  };

  const playRadio = () => {
    getPlaylistRadio(true);
    setPlayed(1); // triggers the eq gif
  };

  useEffect(() => {
    setPlaying(globalState.state.playing);
    setPaused(globalState.state.paused);
    setCurrentTrack(getCurrentTrack(globalState.state));
    setPlaylist({
      name: globalState.state.name,
      tracks: globalState.state.tracks,
      autoAdvance: globalState.state.autoAdvance,
    });
    if (globalState.state.seeking) {
      handleSeek(globalState.state.seekTo);
      dispatch({ type: "track_seek_complete" });
    }
  }, [globalState]);

  const playerToggle = () => {
    dispatch({ type: "toggle" });
  };

  const playerStop = () => {
    dispatch({ type: "stop" });
  };
  const playerForward = () => {
    dispatch({ type: "forward" });
  };
  const playerReverse = () => {
    dispatch({ type: "rewind" });
  };
  const handleSeek = (s) => {
    thePlayer.current.seekTo(s);
  };

  // Handle progress slider click
  const handleSliderClick = (e) => {
    if (!playing || isRadio) return;
    const progress = calculateSeekProgress(e, progressSliderRef);
    dispatch({ type: "track_seek", seekTo: progress });
  };

  // Handle progress slider drag start
  const handleSliderMouseDown = (e) => {
    if (!playing || isRadio) return;
    setIsDragging(true);
    const progress = calculateSeekProgress(e, progressSliderRef);
    dispatch({ type: "track_seek", seekTo: progress });
  };

  // Handle progress slider drag
  const handleSliderMouseMove = useCallback((e) => {
    if (!isDragging || !playing || isRadio) return;
    const progress = calculateSeekProgress(e, progressSliderRef);
    dispatch({ type: "track_seek", seekTo: progress });
  }, [isDragging, playing, isRadio, dispatch]);

  // Handle progress slider drag end
  const handleSliderMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleSliderMouseMove);
      window.addEventListener("mouseup", handleSliderMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleSliderMouseMove);
        window.removeEventListener("mouseup", handleSliderMouseUp);
      };
    }
  }, [isDragging, handleSliderMouseMove, handleSliderMouseUp]);

  // Trigger AddToPlaylistModal when showAddToPlaylistModal is true
  useEffect(() => {
    if (showAddToPlaylistModal && addToPlaylistModalRef.current) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        // Find the button inside the modal component and click it
        const button = addToPlaylistModalRef.current.querySelector('.addToPlaylist');
        if (button) {
          // Temporarily enable pointer events to allow clicking
          const container = addToPlaylistModalRef.current;
          container.style.pointerEvents = 'auto';
          button.click();
          container.style.pointerEvents = 'none';
          setShowAddToPlaylistModal(false);
        }
      }, 0);
    }
  }, [showAddToPlaylistModal]);

  const handleProgress = (state) => {
    let progressPercent = 0;
    if (state.played > 0) {
      progressPercent = Math.round(100 - (1 - state.played) * 100);
    }
    //if (progressPercent % 2 === 0) {
    dispatch({ type: "track_progress", progress: progressPercent });
    //}

    setPlayed(state.played);

    // sometimes .played finishes at 0, sometime at 1. this covers both cases
    // if (state.played >= 1 || (playing && state.played === 0 && state.loaded > 0)) {
    // if user wants a custom playlist its safe to assume the player should continue forward after each track
    if (trackCompleted(state)) {
      settings.autoplayInJukebox || playlist.autoAdvance || playlist.name == "Custom Playlist" ? playerForward() : playerStop();
    }
  };

  const handleDuration = (duration) => {
    setDuration(duration);
  };

  const handleError = (currentTrack) => {
    // youtube video will not play, so we need to forward the playlist
    // for now, just pause the playlist
    // playerForward();
    // add google analytics event
    window.gtag('event', 'youtube_video_error', {
      event_category: 'youtube',
      event_label: 'video_error',
      value: currentTrack?.id || 'unknown'
    });
  };

  const handleVideoFullscreen = useCallback(() => {
    const el = videoContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      const t = thePlayer.current?.getCurrentTime?.();
      if (typeof t === "number") setSeekToOnReady(t);
      setIsVideoFullscreen(true);
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
      else if (el.msRequestFullscreen) el.msRequestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => {
      const entering = document.fullscreenElement === videoContainerRef.current;
      if (!entering) {
        const t = thePlayer.current?.getCurrentTime?.();
        if (typeof t === "number") setSeekToOnReady(t);
      }
      setIsVideoFullscreen(entering);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    document.addEventListener("msfullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
      document.removeEventListener("msfullscreenchange", onFullscreenChange);
    };
  }, []);

  const handleYouTubeReady = useCallback(() => {
    if (seekToOnReady != null && thePlayer.current) {
      thePlayer.current.seekTo(seekToOnReady);
      setSeekToOnReady(null);
    }
  }, [seekToOnReady]);

  const isLoggedin = () => userState && userState.loggedin;
  const isLoggedinChim = () => userState && userState.loggedin && userState.isChim;

  const isRadio = currentTrack.media_type === "radio";

  // Fetch current RadioKing track when Dream Chimney Radio is playing
  useEffect(() => {
    if (!isRadio) {
      setRadioCurrentTrack(null);
      return;
    }
    const fetchRadioCurrentTrack = async () => {
      try {
        const { data } = await axios.get(RADIO_CURRENT_TRACK_URL);
        if (data && (data.artist != null || data.title != null)) {
          setRadioCurrentTrack({ artist: data.artist || "", title: data.title || "" });
        }
      } catch (err) {
        setRadioCurrentTrack(null);
      }
    };
    fetchRadioCurrentTrack();
    const interval = setInterval(fetchRadioCurrentTrack, 30000); // fetch every 30 seconds
    return () => clearInterval(interval);
  }, [isRadio]);

  // not ready, pause button goes into infinity loop
  // window.addEventListener("keydown", function (e) {
  //   if (e.keyCode === 32 && e.target == document.body) {
  //     e.preventDefault();
  //     if (playing) {
  //       console.log("Pause");
  //       playerToggle();
  //     }
  //   }
  // });

  return (
    <div>
      <div className="jukeboxTab">Dream Chimney: Jukebox</div>
      <footer>
        <Container className="" id="totd-jukebox">
          <Row className="jukebox-inner">
            {playing && currentTrack.sc_url && isYouTubeMediaUrl(currentTrack) && (
              <div className="jukebox-zoom-expand d-none d-sm-block">
                <FontAwesomeIcon
                  onClick={() => setVideoExpand(!videoExpand)}
                  icon={videoExpand ? faSearchMinus : faSearchPlus}
                  style={{ position: "absolute", top: 5, left: 5 }}
                  title={videoExpand ? "Zoom out" : "Zoom in"}
                />
                <FontAwesomeIcon
                  onClick={handleVideoFullscreen}
                  icon={faExpandAlt}
                  style={{ position: "absolute", top: 5, left: 32 }}
                  title="Full screen"
                />
              </div>
            )}
            <Col xs={12} sm={8} className="col-left-side">
              <div className="totd-player">
                <div className="totd-player-inner">
                  <Col xs={5} sm={8} md={5} className="totd-player-controls col-left">
                    <button onClick={() => playerToggle()} disabled={loading}>
                      {playing ? <FontAwesomeIcon icon={faPause} /> : <FontAwesomeIcon icon={faPlay} />}
                    </button>
                    <button onClick={() => playerStop()}>
                      <FontAwesomeIcon icon={faStop} />
                    </button>
                    <button onClick={() => playerReverse()} disabled={isRadio}>
                      <FontAwesomeIcon icon={faBackward} />
                    </button>
                    <button onClick={() => playerForward()} disabled={isRadio}>
                      <FontAwesomeIcon icon={faForward} />
                    </button>

                    <div>
                      <div style={{ marginTop: "5px" }}>
                        <FontAwesomeIcon icon={faVolumeUp} />
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="any"
                          value={volume}
                          onChange={(e) => setVolume(parseFloat(e.target.value))}
                        />
                      </div>
                    </div>
                  </Col>

                  <Col xs={7} sm={10} md={8} className="now-playing col-middle">
                    {playing && (
                      <div>
                        {isRadio ? (
                          <span className="now-playing-thumbnail">
                            <a
                              totd-track-event="DCJukeboxRadio"
                              href="//www.dreamchimney.com/radio"
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open Dream Chimney Radio website"
                            >
                              <img alt="Currently Playing" src={`//www.dreamchimney.com/tracks/track_images/dc_radio_logo_sm.jpg`} />
                            </a>
                          </span>
                        ) : (
                          <Link to={`/${currentTrack.id}`} className="now-playing-thumbnail">
                            <TrackImage track={currentTrack} alt="Currently Playing" link={false} width={null} />
                          </Link>
                        )}

                        <span className="now-playing-heading">
                          {loading && (
                            <span className="loading">
                              <FontAwesomeIcon icon={faSpinner} spin />
                              <span className="sr-only">Loading...</span>
                            </span>
                          )}
                          <p className="now-playing-track">
                            {/* if isRadio then hide this span */}
                            {!isRadio && (
                              <span style={{ marginRight: "5px" }}>
                                <MediaSourceIcon track={currentTrack} />
                              </span>
                            )}
                            {currentTrack.media_type !== "radio" && (
                              <React.Fragment>
                                "<Link to={`/${currentTrack.id}`}>{longText(currentTrack.title, 40)}</Link>" by{" "}
                                {longText(currentTrack.artist, 30)}
                              </React.Fragment>
                            )}
                            {isRadio &&
                              (loading ? (
                                <div>Loading, please hold...</div>
                              ) : (
                                <div>
                                  <span style={{
                                    marginBottom: '6px',
                                    display: "inline-block",
                                    fontSize: '14px',
                                  }}>You are listening to Dream Chimney Radio</span>
                                  {radioCurrentTrack && (radioCurrentTrack.artist || radioCurrentTrack.title) && (
                                    <span style={{ fontSize: '16px' }}>
                                      <br />Now Playing: {" "}
                                      {radioCurrentTrack.title && <span>
                                        {radioCurrentTrack.artist && <span>{longText(radioCurrentTrack.artist, 30)}</span>}{" "}
                                        "{longText(radioCurrentTrack.title, 40)}"</span>}
                                    </span>
                                  )}
                                </div>
                              ))}
                          </p>
                          {!isRadio && (
                            <>
                              <div className="time" style={{ display: "none" }}>
                                (<Duration seconds={duration * played} /> / <Duration seconds={duration} />)
                              </div>
                              {playing && duration > 0 && (
                                <ProgressSliderWrapper>
                                  <TimeLabel align="left">
                                    <Duration seconds={duration * played} />
                                  </TimeLabel>
                                  <ProgressBarContainer
                                    ref={progressSliderRef}
                                    onClick={handleSliderClick}
                                    onMouseDown={handleSliderMouseDown}
                                  >
                                    <ProgressBarFill
                                      progress={globalState.state.progress || 0}
                                    />
                                  </ProgressBarContainer>
                                  <TimeLabel align="right">
                                    <Duration seconds={duration} />
                                  </TimeLabel>
                                </ProgressSliderWrapper>
                              )}
                            </>
                          )}
                        </span>
                        {/* {played > 0 && (
                          <div className="eq">
                            <img src="//www.dreamchimney.com/tracks/jukebox/eq.gif" alt="Track is playing" />
                          </div>
                        )} */}
                      </div>
                    )}
                  </Col>
                </div>
              </div>
            </Col>
            <Col xs={0} sm={0} md={4} className="col-right d-none d-md-block">
              <div style={{ marginBottom: '10px' }}>
                <div style={{ margin: '0 auto', textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faThList} /> <i className="fa fa-list" aria-hidden="true"></i> Playlist:
                  <select className="playlistSelect" onChange={handleChangePlaylist}>
                    <option selected>
                      {playlist.name} ({playlist.tracks && playlist.tracks.length} tracks)
                    </option>
                    <option disabled>-----</option>
                    {/* <option value="radio">Dream Chimney Radio</option> */}
                    <option value="most_recent">Most Recent Tracks</option>
                    {isLoggedinChim() && (
                      <>
                        <option value="favorites">My Favorite tracks</option>
                      </>
                    )}
                    {isLoggedinChim() && (
                      <>
                        <option value="user_most_recent">My Most Recent Tracks</option>
                      </>
                    )}
                    {isLoggedin() && favoritePlaylists.length > 0 && (
                      <>
                        <option disabled>-----</option>
                        {favoritePlaylists.map((p) => (
                          <option key={p.id} value={`fav-playlist-${p.id}`}>
                            {p.name} ({p.total} tracks)
                          </option>
                        ))}
                      </>
                    )}
                    <option disabled>-----</option>
                    <option
                      value="add_to_playlist"
                      disabled={!playing || !currentTrack.id || isRadio || !isLoggedinChim()}
                      style={{
                        color: (!playing || !currentTrack.id || isRadio || !isLoggedinChim()) ? '#999' : '#fff'
                      }}
                    >
                      Add Current Track to Playlist
                    </option>

                  </select>
                </div>

              </div>
              {!playing && (
                <div onClick={() => playRadio()} className="playRadioButton">
                  <FontAwesomeIcon icon={faPlay} style={{ marginRight: '5px' }} /> Play Dream Chimney Radio
                </div>
              )}
              {playing && played > 0 && (
                <div className="eq">
                  <img src="//www.dreamchimney.com/tracks/jukebox/eq.gif" alt="Track is playing" />
                </div>
              )}

            </Col>

          </Row>
          <div className="jukebox-outer">
            <div id="jukebox-video-wrapper"></div>
          </div>
        </Container>
      </footer>

      {currentTrack.media_type === "radio" && (
        <div style={{ display: "none" }}>
          <audio autoplay="autoplay" controls>
            <source src="https://www.radioking.com/play/dream-chimney-radio" type="audio/mpeg" />
          </audio>
        </div>
      )}

      {currentTrack.sc_url && currentTrack.media_type !== "radio" && isSoundCloudMediaUrl(currentTrack) ? (
        <SoundCloud
          id="player"
          ref={thePlayer}
          volume={volume}
          className={"player-hide"}
          url={currentTrack.sc_url}
          playing={playing}
          onProgress={handleProgress}
          onDuration={handleDuration}
          onError={handleError}
        />
      ) : (
        <div ref={videoContainerRef} className="jukebox-youtube-container">
          <YouTubePlayer
            key={isVideoFullscreen ? "yt-fullscreen" : "yt-normal"}
            ref={thePlayer}
            id="player"
            volume={volume}
            width={isVideoFullscreen ? "100%" : undefined}
            height={isVideoFullscreen ? "100%" : undefined}
            controls={isVideoFullscreen}
            className={!playing && !paused ? "player-hide" : videoExpand ? "player-show-max" : ""}
            url={currentTrack.sc_url}
            playing={playing}
            onReady={handleYouTubeReady}
            onProgress={handleProgress}
            onDuration={handleDuration}
            onError={handleError}
          />
          {isVideoFullscreen && (
            <button
              type="button"
              className="jukebox-exit-fullscreen"
              onClick={() => {
                if (document.exitFullscreen) document.exitFullscreen();
                else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
              }}
              title="Exit full screen"
            >
              <FontAwesomeIcon icon={faExpandArrows} /> Exit full screen
            </button>
          )}
        </div>
      )}

      {/* Add to Playlist Modal */}
      {currentTrack.id && !isRadio && (
        <div ref={addToPlaylistModalRef} style={{ position: "absolute", visibility: "hidden", pointerEvents: "none" }}>
          <AddToPlaylistModal trackId={currentTrack.id} />
        </div>
      )}
    </div>
  );
}

export default Jukebox;
